import { useMutation } from "@apollo/client";
import { DateTime } from "luxon";
import { CALENDAR_APPOINTMENTS_QUERY } from "@/domains/appointment/calendar/graphql/calendar";
import { useSalonConfig } from "@/domains/settings/features/salon-config/hooks/use-salon-config";
import type { CreateAppointmentInput, ServiceSelectionInput } from "@/gql/graphql";
import {
  APPOINTMENTS_QUERY,
  CREATE_APPOINTMENT_MUTATION,
  DASHBOARD_METRICS_QUERY,
  WAITING_TO_CONFIRM_APPOINTMENTS_QUERY,
} from "@/graphql/appointments";
import { useSalonStore } from "@/lib/store/salon";
import type { SelectedService } from "./use-services-step";

interface UseAppointmentCreationProps {
  onSuccess?: () => void;
  onClose?: () => void;
}

export function useAppointmentCreation({ onSuccess, onClose }: UseAppointmentCreationProps = {}) {
  const { currentSalon } = useSalonStore();
  const { config } = useSalonConfig();

  const [createAppointment, { loading, error }] = useMutation(CREATE_APPOINTMENT_MUTATION, {
    onCompleted: () => {
      onClose?.();
      onSuccess?.();
    },
    refetchQueries: [
      APPOINTMENTS_QUERY,
      CALENDAR_APPOINTMENTS_QUERY,
      WAITING_TO_CONFIRM_APPOINTMENTS_QUERY,
      DASHBOARD_METRICS_QUERY,
    ],
  });

  const handleCreateAppointment = async ({
    customerId,
    selectedServices,
    startTime,
    notes,
  }: {
    customerId: string;
    selectedServices: SelectedService[];
    startTime: DateTime;
    notes: string;
  }) => {
    if (!currentSalon?.id) {
      throw new Error("No salon selected");
    }

    const serviceSelections: ServiceSelectionInput[] = selectedServices.map((service) => {
      const selection = {
        serviceId: service.id,
        selectedOptionIds: service.selectedOptions || [],
        workerId: config.appointmentWorkerAssignmentEnabled ? service.workerId || null : null,
      };

      return selection;
    });

    const startTimeISO = startTime.toISO();
    if (!startTimeISO) {
      throw new Error("Invalid start time");
    }

    const input: CreateAppointmentInput = {
      customerId,
      startTime: startTimeISO,
      serviceSelections,
      notes: notes || undefined,
      bookingStatus: "CONFIRMED",
    };

    try {
      await createAppointment({
        variables: {
          salonId: currentSalon.id,
          input,
        },
      });
    } catch (error) {
      console.error("Error creating appointment:", error);
      throw error;
    }
  };

  return {
    handleCreateAppointment,
    loading,
    error,
  };
}
